import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Button, TextField } from '@/components';
import { ComplianceTypes, INVITE_FRIEND_CREDIT_COUNT } from '@/constants';
import { useForm } from '@/hooks';
import {
  selectUserCompliance,
  useSendFreeTrialShareEmailMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export type ShareWithAFriendFormState = {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
};

export const ShareWithAFriendForm = ({
  onSuccess,
  showSuccessView = false,
}: {
  onSuccess?: () => void;
  showSuccessView?: boolean;
}) => {
  const compliance = useSelector(selectUserCompliance);

  const [sendShareEmail, { isLoading }] = useSendFreeTrialShareEmailMutation();
  const [isSuccessShown, setIsSuccessShown] = useState(false);

  const { search } = useLocation();

  const { form, errors, validateForm, onChange } =
    useForm<ShareWithAFriendFormState>({
      firstName: '',
      lastName: '',
      email: '',
      message: `I've been using LumiQ and thought you'd like it too. It's ${compliance} that works through podcasts, so you can listen on your commute, lunch break, or whenever you have time. You can listen on your computer or their mobile app!`,
    });

  const onSubmit = async () => {
    const isValid = validateForm(['firstName', 'lastName', 'email']);

    if (!isValid) {
      return;
    }

    const queryParams = Object.fromEntries(
      new URLSearchParams(search).entries(),
    );

    try {
      await sendShareEmail({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        message: form.message,
        compliance,
        queryParams:
          queryParams.pass_through === 'true' ? queryParams : undefined,
      }).unwrap();
      onSuccess?.();
      if (showSuccessView) {
        setIsSuccessShown(true);
      } else {
        SnackbarUtils.success(
          `Email sent to ${form.firstName} ${form.lastName}!`,
        );
      }
    } catch {
      SnackbarUtils.error(
        'Something went wrong. Please try again or contact support.',
      );
    }
  };

  if (isSuccessShown) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: 500,
          gap: 1.5,
        }}>
        <Typography variant="h2">Email sent!</Typography>
        <Typography>Know someone else who could benefit from LumiQ?</Typography>
        <Button
          variant="primary"
          label="Share Again"
          skinny={false}
          onClick={() => setIsSuccessShown(false)}
          fullWidth={false}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        color: 'text.main',
        gap: 1.5,
      }}>
      <Box>
        <Typography variant="h2">Share With a Friend</Typography>
        <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
          Invite a friend to try LumiQ and get them started with{' '}
          {INVITE_FRIEND_CREDIT_COUNT} free{' '}
          {compliance === ComplianceTypes.CPD ? 'hour' : 'credit'}s!
        </Typography>
      </Box>
      <Box
        sx={{
          gap: 1,
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
        }}>
        <TextField
          id="firstName"
          fullWidth
          label="First Name"
          value={form.firstName}
          error={!!errors.firstName}
          helperText={errors.firstName}
          onChange={onChange}
          sx={{ flex: 1 }}
        />
        <TextField
          id="lastName"
          fullWidth
          label="Last Name"
          value={form.lastName}
          error={!!errors.lastName}
          helperText={errors.lastName}
          onChange={onChange}
          sx={{ flex: 1 }}
        />
      </Box>
      <TextField
        id="email"
        fullWidth
        label="Email"
        value={form.email}
        error={!!errors.email}
        helperText={errors.email}
        onChange={onChange}
      />
      <TextField
        id="message"
        fullWidth
        label="Message"
        value={form.message}
        error={!!errors.message}
        helperText={errors.message}
        onChange={onChange}
        multiline
        rows={5}
        slotProps={{
          input: { spellCheck: false },
        }}
      />
      <Button
        variant="primary"
        label="Share LumiQ"
        skinny={false}
        onClick={onSubmit}
        fullWidth={false}
        isLoading={isLoading}
        sx={{
          mt: 'auto',
          ml: {
            xs: 0,
            sm: 'auto',
          },
        }}
      />
    </Box>
  );
};
